// WHY — big single-line chart used by the expanded state of each MetricTile
// on the account page. Same axis-label + gridline treatment as
// SmartOverlayChart, but for one metric instead of overlaid accounts.
// Caller supplies the Y formatter so $ / counts / % all render correctly.

import { useMemo } from "react";
import {
  CHART_MARGIN_LEFT,
  CHART_MARGIN_RIGHT,
  CHART_MARGIN_TOP,
  CHART_MARGIN_BOTTOM,
  buildYTicks,
  buildXTickIndices,
  fmtAxisDate,
  fmtAxisValue,
} from "../lib/chartAxes";

interface Props {
  points: Array<{ date: string; value: number | null }>;
  width?: number;
  height?: number;
  stroke?: string;
  formatY?: (v: number) => string;
}

export function ExpandedMetricChart({
  points,
  width = 600,
  height = 160,
  stroke = "var(--color-bone)",
  formatY,
}: Props) {
  const { yMin, yMax } = useMemo(() => {
    let lo = Infinity;
    let hi = -Infinity;
    for (const p of points) {
      if (p.value !== null && Number.isFinite(p.value)) {
        if (p.value < lo) lo = p.value;
        if (p.value > hi) hi = p.value;
      }
    }
    if (!Number.isFinite(lo)) return { yMin: 0, yMax: 1 };
    const range = hi - lo;
    const pad = Math.max(range * 0.1, 1);
    return { yMin: Math.max(0, lo - pad), yMax: hi + pad };
  }, [points]);

  const isEmpty =
    points.length === 0 ||
    points.every((p) => p.value === null || !Number.isFinite(p.value));
  if (isEmpty) {
    return (
      <div
        style={{
          height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--color-bone-dim)",
          background: "rgba(255,255,255,0.02)",
          borderRadius: 4,
          fontFamily: "var(--font-mono)",
          fontSize: "var(--fs-xs)",
        }}
      >
        No data
      </div>
    );
  }

  const yRange = yMax - yMin || 1;
  const chartLeft = CHART_MARGIN_LEFT;
  const chartRight = width - CHART_MARGIN_RIGHT;
  const chartTop = CHART_MARGIN_TOP;
  const chartBottom = height - CHART_MARGIN_BOTTOM;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;

  const days = points.length;
  const yScale = (v: number): number =>
    chartTop + ((yMax - v) / yRange) * chartHeight;
  const xScale = (i: number): number =>
    chartLeft + (i / Math.max(days - 1, 1)) * chartWidth;

  let d = "";
  let inSegment = false;
  for (let i = 0; i < days; i++) {
    const p = points[i];
    if (!p || p.value === null || !Number.isFinite(p.value)) {
      inSegment = false;
      continue;
    }
    const x = xScale(i);
    const y = yScale(p.value);
    if (!inSegment) {
      d += `M ${x} ${y}`;
      inSegment = true;
    } else {
      d += ` L ${x} ${y}`;
    }
  }

  const yTicks = buildYTicks(yMin, yMax);
  const xTickIndices = buildXTickIndices(days);
  const labelY = (v: number) => (formatY ? formatY(v) : fmtAxisValue(v, yRange));

  const axisLabelStyle = {
    position: "absolute" as const,
    fontSize: "var(--fs-xs)",
    fontFamily: "var(--font-mono)",
    color: "var(--color-bone-dim)",
    pointerEvents: "none" as const,
    userSelect: "none" as const,
    lineHeight: 1,
  };

  return (
    <div style={{ position: "relative", height }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: "block" }}
      >
        {yTicks.map((tick) => (
          <line
            key={`grid-${tick}`}
            x1={chartLeft}
            x2={chartRight}
            y1={yScale(tick)}
            y2={yScale(tick)}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
        <line
          x1={chartLeft}
          x2={chartRight}
          y1={chartBottom}
          y2={chartBottom}
          stroke="rgba(255,255,255,0.12)"
          strokeWidth={1}
          vectorEffect="non-scaling-stroke"
        />
        {d && (
          <path
            d={d}
            fill="none"
            stroke={stroke}
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
      </svg>

      {yTicks.map((tick) => (
        <span
          key={`y-${tick}`}
          style={{
            ...axisLabelStyle,
            left: 0,
            top: `${(yScale(tick) / height) * 100}%`,
            transform: "translateY(-50%)",
            width: CHART_MARGIN_LEFT - 6,
            textAlign: "right",
          }}
        >
          {labelY(tick)}
        </span>
      ))}

      {xTickIndices.map((idx) => {
        const dateStr = points[idx]?.date ?? "";
        return (
          <span
            key={`x-${idx}`}
            style={{
              ...axisLabelStyle,
              top: chartBottom + 6,
              left: `${(xScale(idx) / width) * 100}%`,
              transform: "translateX(-50%)",
              whiteSpace: "nowrap",
            }}
          >
            {fmtAxisDate(dateStr)}
          </span>
        );
      })}
    </div>
  );
}
